import { useRef, FC } from 'react'
import { connect } from 'react-redux'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import styled from 'styled-components'

import useDispatch from 'hooks/useDispatch'
import { signInUser } from 'actions/authActions'
import { Submit } from 'components/form'
import { StyledLink } from 'components/display/Typography'

import AuthTextField from './AuthTextField'
import { AuthToken, FacebookLogin, GoogleLogin, AppleLogin } from './shared'
import { Divider, FieldWrapper } from './styles'

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  font-weight: 300;
  gap: 8px;
`

interface FormValues {
  user: { email: string; password: string; remember_me: boolean }
}

interface LoginProps {
  auth: { loading: boolean }
  onSuccess?: () => any
  onConfirmEmail: () => any
  onResetPassword: () => any
  serverSide?: boolean
}

const validationSchema = Yup.object({
  user: Yup.object({
    email: Yup.string()
      .required('This field is required')
      .email('Not a valid email'),
    password: Yup.string().required('This field is required'),
    remember_me: Yup.boolean(),
  }),
})

const Login: FC<LoginProps> = ({
  auth,
  onSuccess,
  onConfirmEmail,
  onResetPassword,
  serverSide = false,
}) => {
  const dispatch = useDispatch()
  const formElement = useRef<HTMLFormElement>(null)

  const form = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      user: { email: '', password: '', remember_me: true },
    },
  })

  const onSubmit = (formValues: FormValues) => {
    if (serverSide) {
      formElement.current?.submit()
    } else {
      // @ts-ignore -- signInUser not typed
      dispatch(signInUser(formValues)).then(onSuccess)
    }
  }

  const { handleSubmit, register } = form

  return (
    <FormProvider {...form}>
      <AppleLogin>Sign in with Apple</AppleLogin>
      <GoogleLogin>Sign in with Google</GoogleLogin>
      <FacebookLogin
        serverSide={serverSide}
        onSuccess={onSuccess}
        onConfirmEmail={onConfirmEmail}
      >
        Sign in with Facebook
      </FacebookLogin>

      <form
        className="qa-login"
        action="/users/sign_in"
        method="post"
        ref={formElement}
        onSubmit={handleSubmit(onSubmit)}
      >
        <AuthToken />

        <Divider>
          <span>or</span>
        </Divider>

        <FieldWrapper>
          <AuthTextField
            label="Email"
            name="user[email]"
            type="email"
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper>
          <AuthTextField
            label="Password"
            name="user[password]"
            type="password"
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper margin="medium">
          <CheckboxWrapper>
            <input type="checkbox" {...register('user[remember_me]' as any)} />{' '}
            Remember me
          </CheckboxWrapper>
        </FieldWrapper>

        <FieldWrapper margin="medium">
          <Submit
            disabled={auth.loading}
            fullwidth
            uppercase={false}
            aria-label="Submit login form"
          >
            Login
          </Submit>
        </FieldWrapper>

        <StyledLink
          as="button"
          type="button"
          onClick={onResetPassword}
          style={{ textAlign: 'center', display: 'block', margin: '0 auto' }}
        >
          Forgot password?
        </StyledLink>
      </form>
    </FormProvider>
  )
}

export default connect(({ auth }: { auth: { loading: boolean } }) => ({
  auth,
}))(Login)
